import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { ArrowLeft, Dumbbell, Clock, Target, Play, Check, Video, Sparkles } from 'lucide-react';

interface WorkoutDetailProps {
  templateId: string;
  onBack: () => void;
  onStartWorkout: (templateId: string) => void;
}

interface WorkoutTemplate {
  id: string;
  name: string;
  type: string;
  level: string | null;
  description: string | null;
}

interface Exercise {
  id: string;
  exercise_name: string;
  sets: number;
  reps: string;
  rest_seconds: number;
  tips: string | null;
  order: number;
  video_url?: string | null;
}

export default function WorkoutDetail({ templateId, onBack, onStartWorkout }: WorkoutDetailProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [template, setTemplate] = useState<WorkoutTemplate | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [isActive, setIsActive] = useState(false);
  const BG_DARK = '#0D0D0D';

  const loadWorkoutDetail = useCallback(async () => {
    const { data: templateData } = await supabase
      .from('workout_templates')
      .select('*')
      .eq('id', templateId)
      .single();

    const { data: exercisesData } = await supabase
      .from('workout_exercises')
      .select('*')
      .eq('workout_template_id', templateId)
      .order('order', { ascending: true });

    if (templateData) setTemplate(templateData);
    if (exercisesData) setExercises(exercisesData);

    setLoading(false);
  }, [templateId]);

  const checkIfActive = useCallback(async () => {
    if (!user) return;

    const { data } = await supabase
      .from('user_workouts')
      .select('id')
      .eq('user_id', user.id)
      .eq('workout_template_id', templateId)
      .eq('is_active', true)
      .maybeSingle();

    setIsActive(!!data);
  }, [user, templateId]);

  useEffect(() => {
    loadWorkoutDetail();
    checkIfActive();
  }, [templateId, loadWorkoutDetail, checkIfActive]);

  const handleActivate = async () => {
    if (!user || !template) return;

    setActivating(true);

    await supabase
      .from('user_workouts')
      .update({ is_active: false })
      .eq('user_id', user.id);

    await supabase.from('user_workouts').upsert({
      user_id: user.id,
      workout_template_id: template.id,
      is_active: true,
      scheduled_days: [],
    });

    setIsActive(true);
    setActivating(false);
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      old_school: 'Old School',
      low_volume: 'Low Volume',
      fisiculturistas: 'Fisiculturistas',
      custom: 'Personalizado',
    };
    return labels[type] || type;
  };

  const getLevelLabel = (level: string | null) => {
    if (!level) return '';
    const labels: Record<string, string> = {
      iniciante: 'Iniciante',
      intermediario: 'Intermediário',
      avancado: 'Avançado',
    };
    return labels[level] || level;
  };



  const getTotalTime = () => {
    const exerciseTime = exercises.reduce((total, ex) => {
      const restTime = ex.rest_seconds * ex.sets;
      const workTime = 45 * ex.sets;
      return total + restTime + workTime;
    }, 0);

    return Math.ceil(exerciseTime / 60);
  };

  const extractTechnique = (reps: string, tips?: string | null) => {
    // Detecta técnica no padrão: "4x8 (Dropset)" ou dentro das dicas
    const match = reps.match(/\(([^)]+)\)/);
    if (match) return match[1];
    if (tips) {
      const candidates = ['Dropset', 'Rest-Pause', 'Hash Pause', 'Superset', 'Giant Set'];
      const found = candidates.find((c) => tips.toLowerCase().includes(c.toLowerCase()));
      if (found) return found;
    }
    return null;
  };

  if (loading || !template) {
    return (
      <div className="min-h-screen flex items-center justify-center pb-20" style={{ backgroundColor: BG_DARK }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#b30000' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: BG_DARK }}>
      <div className="mx-auto px-4 py-8 max-w-4xl">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-[#B0B0B0] hover:text-white transition mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar</span>
        </button>

        <div className="rounded-2xl p-8 mb-6 bg-[#141414] border border-white/10">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className={`px-3 py-1 rounded text-sm font-semibold text-white bg-white/10`}>
                  {getTypeLabel(template.type)}
                </span>
                {template.level && (
                  <span className="px-3 py-1 rounded text-sm font-medium bg-white/5 text-[#B0B0B0] border border-white/10">
                    {getLevelLabel(template.level)}
                  </span>
                )}
                {isActive && (
                  <span className="px-3 py-1 rounded text-sm font-medium bg-white/10 text-white flex items-center gap-1 border border-white/20">
                    <Check className="w-4 h-4" />
                    Ativo
                  </span>
                )}
              </div>
              <h1 className="text-3xl font-bold text-white mb-3">{template.name}</h1>
              {template.description && (
                <p className="text-[#B0B0B0] leading-relaxed">{template.description}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-6 text-[#B0B0B0] mb-6">
            <div className="flex items-center gap-2">
              <Dumbbell className="w-5 h-5" />
              <span>{exercises.length} exercícios</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>{getTotalTime()} min</span>
            </div>
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              <span>{exercises.reduce((total, ex) => total + ex.sets, 0)} séries</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => onStartWorkout(templateId)}
              className="flex-1 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition flex items-center justify-center gap-2 border border-white/20"
            >
              <Play className="w-5 h-5" />
              Começar Treino
            </button>
            {!isActive && (
              <button
                onClick={handleActivate}
                disabled={activating}
                className="px-6 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed border border-white/20"
              >
                {activating ? 'Ativando...' : 'Ativar Plano'}
              </button>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <h2 className="text-xl font-bold text-white mb-4">Exercícios</h2>
          {exercises.map((exercise, index) => (
            <div key={exercise.id} className="rounded-xl p-6 bg-[#141414] border border-white/10">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center font-bold text-white flex-shrink-0 border border-white/20">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2">{exercise.exercise_name}</h3>
                  <div className="flex items-center gap-4 text-sm text-[#B0B0B0] mb-3">
                    <span className="font-medium">{exercise.sets} séries</span>
                    <span>•</span>
                    <span>{exercise.reps} reps</span>
                    <span>•</span>
                    <span>{exercise.rest_seconds}s descanso</span>
                  </div>
                  {/* Técnica detectada */}
                  {extractTechnique(exercise.reps, exercise.tips) && (
                    <div className="flex items-center gap-2 text-xs text-white mb-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/10 border border-white/20">
                        <Sparkles className="w-3 h-3" />
                        Técnica: {extractTechnique(exercise.reps, exercise.tips)}
                      </span>
                    </div>
                  )}

                  {/* Vídeo de execução */}
                  {exercise.video_url ? (
                    <div className="rounded-lg overflow-hidden mb-3 border border-white/10">
                      <video
                        src={exercise.video_url}
                        className="w-full h-48 object-cover"
                        muted
                        loop
                        playsInline
                        controls
                      />
                    </div>
                  ) : (
                    <div className="rounded-lg p-3 mb-3 bg-white/5 border border-white/10 flex items-center gap-2 text-[#B0B0B0] text-sm">
                      <Video className="w-4 h-4" />
                      Vídeo de execução em breve
                    </div>
                  )}

                  {exercise.tips && (
                    <div className="rounded-lg p-3 bg-white/5 border border-white/10">
                      <p className="text-sm text-[#B0B0B0] leading-relaxed">{exercise.tips}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
